import { effect, Signal } from '@preact/signals';
import type { ObjectValues, StorageDefinition } from 'utils/types.ts';
import type { PageType } from 'lib/dot/enum/pageType.ts';
import omitBy from 'lodash/omitBy';
import type { OfferDetailMeta } from 'lib/dot/offerDetail/utils.ts';
import type { SearchMeta } from 'lib/dot/search/utils.ts';
import { useMemo } from 'preact/hooks';

export const SessionStorageKeys = {
	FRAUD_WARNING_HIDDEN: 'hideFraudWarning',
	OFFER_ID: 'offerId',
	SEARCH_META: 'searchMeta',
	OFFER_DETAIL_META: 'offerDetailMeta',
	LATEST_PAGE_TYPE: 'latestPage',
	ACTIVE_HP_TAB: 'activeHpTab',
	TOPPED_OFFERS: 'toppedOffers',
	HISTORY: 'h',
	HISTORY_SEQUENCE: 's',
} as const;

/**
 * Kompletní seznam Session Storage klíčů používaných aplikací Sbazar
 */
export const SBAZAR_SESSION_STORAGE: StorageDefinition[] = [
	{
		name: SessionStorageKeys.FRAUD_WARNING_HIDDEN,
		description: 'Stav skrytí varování před podvodnými inzeráty',
		type: 'Preferenční',
		expiration: 'Do zavření prohlížeče',
	},
	{
		name: SessionStorageKeys.OFFER_ID,
		description: 'Identifikátor aktuálně zobrazovaného inzerátu (dynamický klíč)',
		type: 'Technická',
		expiration: 'Do zavření prohlížeče',
	},
	{
		name: SessionStorageKeys.SEARCH_META,
		description: 'Metadata o posledním vyhledávání pro analytické účely',
		type: 'Analytická',
		expiration: 'Do zavření prohlížeče',
	},
	{
		name: SessionStorageKeys.OFFER_DETAIL_META,
		description: 'Metadata o zobrazení detailu inzerátu pro analytické účely',
		type: 'Analytická',
		expiration: 'Do zavření prohlížeče',
	},
	{
		name: SessionStorageKeys.LATEST_PAGE_TYPE,
		description: 'Typ poslední navštívené stránky pro navigační logiku',
		type: 'Technická',
		expiration: 'Do zavření prohlížeče',
	},
	{
		name: SessionStorageKeys.ACTIVE_HP_TAB,
		description: 'Aktivní záložka na hlavní stránce',
		type: 'Preferenční',
		expiration: 'Do zavření prohlížeče',
	},
	{
		name: SessionStorageKeys.TOPPED_OFFERS,
		description: 'Seznam topovaných inzerátů pro session',
		type: 'Technická',
		expiration: 'Do zavření prohlížeče',
	},
	{
		name: SessionStorageKeys.HISTORY,
		description: 'Historie navigace v rámci session pro back/forward navigaci',
		type: 'Technická',
		expiration: 'Do zavření prohlížeče',
	},
	{
		name: SessionStorageKeys.HISTORY_SEQUENCE,
		description: 'Sekvence historie pro správné řazení navigačních kroků',
		type: 'Technická',
		expiration: 'Do zavření prohlížeče',
	},
];

export function getSessionStorageList(): StorageDefinition[] {
	return SBAZAR_SESSION_STORAGE;
}

export type SessionStorageKeys =
	| ObjectValues<typeof SessionStorageKeys>
	| `${typeof SessionStorageKeys.OFFER_ID}:${number}`;

export class SessionStorage {
	static removeItem(key: string) {
		if (globalThis?.sessionStorage) {
			globalThis.sessionStorage.removeItem(key);
		}
	}

	static setItem(key: typeof SessionStorageKeys.FRAUD_WARNING_HIDDEN, value: '1'): void;
	static setItem(key: `${typeof SessionStorageKeys.OFFER_ID}:${number}`, value: string): void;
	static setItem(key: typeof SessionStorageKeys.LATEST_PAGE_TYPE, value: PageType): void;
	static setItem(key: typeof SessionStorageKeys.OFFER_DETAIL_META, value: string): void;
	static setItem(key: typeof SessionStorageKeys.SEARCH_META, value: string): void;
	static setItem(key: typeof SessionStorageKeys.LATEST_PAGE_TYPE, value: PageType): void;
	static setItem(key: string, value: string): void;
	static setItem(key: SessionStorageKeys, value: string): void {
		if (globalThis?.sessionStorage) {
			globalThis.sessionStorage.setItem(key, value);
		}
	}

	static getItem(key: typeof SessionStorageKeys.FRAUD_WARNING_HIDDEN): '1' | undefined;
	static getItem(key: `${typeof SessionStorageKeys.OFFER_ID}:${number}`): string | undefined;
	static getItem(key: typeof SessionStorageKeys.LATEST_PAGE_TYPE): PageType | undefined;
	static getItem(key: typeof SessionStorageKeys.OFFER_DETAIL_META): string;
	static getItem(key: typeof SessionStorageKeys.SEARCH_META): string;
	static getItem(key: string): string | undefined;
	static getItem(key: SessionStorageKeys): string | undefined {
		if (globalThis?.sessionStorage) {
			return globalThis.sessionStorage.getItem(key) || undefined;
		}

		return undefined;
	}

	static setOfferDetailMeta(data: OfferDetailMeta) {
		SessionStorage.setItem(
			SessionStorageKeys.OFFER_DETAIL_META,
			JSON.stringify(omitBy(data, (v) => v === undefined)),
		);
	}

	static getOfferDetailMeta(): OfferDetailMeta | undefined {
		const data = SessionStorage.getItem(SessionStorageKeys.OFFER_DETAIL_META);
		if (!data) return undefined;

		try {
			return JSON.parse(data);
		} catch (e) {
			console.error(e);
			return undefined;
		}
	}

	static setSearchMeta(data: SearchMeta) {
		SessionStorage.setItem(SessionStorageKeys.SEARCH_META, JSON.stringify(omitBy(data, (v) => v === undefined)));
	}

	static getSearchMeta(): SearchMeta | undefined {
		const data = SessionStorage.getItem(SessionStorageKeys.SEARCH_META);
		if (!data) return undefined;

		try {
			return JSON.parse(data);
		} catch (e) {
			console.error(e);
			return undefined;
		}
	}
}

type SessionStorageSignalOptions<T> = {
	defaultValue?: T | null;
	transformToData: (value: string | null | undefined) => T;
	transformToString: (value: NonNullable<T>) => string;
};
export class SessionStorageSignal<T> extends Signal<T> {
	key: SessionStorageKeys;

	constructor(
		key: SessionStorageKeys,
		options: SessionStorageSignalOptions<T> = {
			transformToData: (str) => JSON.parse(str ?? 'null'),
			transformToString: (value) => JSON.stringify(value),
		},
	) {
		super(options.transformToData(SessionStorage.getItem(key)) ?? options.defaultValue ?? undefined);
		this.key = key;

		effect(() =>
			this.value === null || this.value === undefined || Number.isNaN(this.value)
				? SessionStorage.removeItem(this.key)
				: SessionStorage.setItem(this.key, options.transformToString(this.value)),
		);
	}
}

export function useSessionStorage<T>(key: SessionStorageKeys, options?: SessionStorageSignalOptions<T>) {
	return useMemo(() => new SessionStorageSignal(key, options), []);
}
